import React, { useCallback, useEffect, useState } from 'react';
import { Rocket, Loader2, RefreshCcw, CheckCircle2, XCircle, AlertTriangle, Ban, ShieldQuestion } from 'lucide-react';
import api from '@/lib/axios';

const STATE_STYLE = {
  'Concluído': ['text-emerald-700 bg-emerald-50 border-emerald-200', CheckCircle2],
  'Incompleto': ['text-amber-700 bg-amber-50 border-amber-200', AlertTriangle],
  'Bloqueado': ['text-red-700 bg-red-50 border-red-200', XCircle],
  'Requer validação': ['text-blue-700 bg-blue-50 border-blue-200', ShieldQuestion],
  'Desativado': ['text-slate-600 bg-slate-100 border-slate-200', Ban],
  'Não aplicável': ['text-slate-500 bg-slate-50 border-slate-200', Ban],
  'configurado': ['text-emerald-700 bg-emerald-50 border-emerald-200', CheckCircle2],
  'em_falta': ['text-amber-700 bg-amber-50 border-amber-200', AlertTriangle],
};

const GROUP_LABELS = {
  identificacao_legal: 'Identificação legal', produtos_precos: 'Produtos e preços',
  stripe: 'Stripe', resend_email: 'Resend e email', juridico_privacidade: 'Jurídico e privacidade',
  google: 'Google', sistema: 'Sistema',
};

function Badge({ estado }) {
  const [cls, Icon] = STATE_STYLE[estado] || ['text-slate-600 bg-slate-100 border-slate-200', ShieldQuestion];
  const isDate = typeof estado === 'string' && estado.includes('T') && estado.includes(':');
  const label = isDate ? new Date(estado).toLocaleString('pt-PT') : estado;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${isDate ? 'text-slate-500 bg-slate-50 border-slate-200' : cls}`}>
      {!isDate && <Icon className="h-3 w-3" />} {label}
    </span>
  );
}

export default function MarketplaceLaunchReadinessPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const res = await api.get('/admin/marketplace/launch-readiness');
      setData(res.data);
    } catch (e) { setErr(e.response?.data?.detail || 'Falha a carregar.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="launch-readiness-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="inline-flex items-center gap-2 text-base font-bold text-slate-900 md:text-lg">
          <Rocket className="h-5 w-5 text-emerald-600" /> Preparação para lançamento
        </h2>
        <button type="button" onClick={load} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-600" data-testid="launch-reload">
          <RefreshCcw className="h-3.5 w-3.5" /> Atualizar
        </button>
      </div>

      {err && <p className="mt-2 text-xs font-semibold text-red-600">{err}</p>}
      {loading ? (
        <p className="mt-4 flex items-center gap-2 text-xs text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> A carregar...</p>
      ) : data && (
        <>
          <div className={`mt-3 rounded-xl border p-3 text-sm font-semibold ${data.pronto_para_lancar ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`} data-testid="launch-global-msg">
            {data.pronto_para_lancar ? '✅ ' : '⚠️ '}{data.mensagem_global}
          </div>

          <div className="mt-3 grid gap-3 md:grid-cols-2">
            {Object.entries(data.grupos || {}).map(([key, g]) => (
              <div key={key} className="rounded-xl border border-slate-200 p-3" data-testid={`launch-group-${key}`}>
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-sm font-bold text-slate-800">{GROUP_LABELS[key] || key}</h3>
                  <Badge estado={g.estado} />
                </div>
                {g.aviso && <p className="mt-1 rounded bg-red-50 px-2 py-1 text-[11px] font-semibold text-red-700">{g.aviso}</p>}
                <ul className="mt-2 space-y-1">
                  {(g.itens || []).map((it, i) => (
                    <li key={i} className="flex items-center justify-between gap-2 text-[11px] text-slate-600">
                      <span>{it.item}{it.detalhe ? ` — ${it.detalhe}` : ''}</span>
                      <Badge estado={it.estado} />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {(data.acoes_dependentes_do_proprietario || []).length > 0 && (
            <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50 p-3" data-testid="launch-owner-actions">
              <h3 className="text-sm font-bold text-blue-900">Ações que dependem de si</h3>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-[11px] text-blue-800">
                {data.acoes_dependentes_do_proprietario.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </div>
          )}

          <p className="mt-3 text-[10px] text-slate-400">Verificado em {data.verificado_em ? new Date(data.verificado_em).toLocaleString('pt-PT') : '—'} · Flags: MARKETPLACE_COMMERCE_ENABLED={String(data.flags?.MARKETPLACE_COMMERCE_ENABLED)}</p>
        </>
      )}
    </section>
  );
}
